
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Divider,
  CircularProgress,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
  Alert,
  Grid,
  Paper,
  Chip,
  // Table components for the list view
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import EditIcon from '@mui/icons-material/Edit';
import StopIcon from '@mui/icons-material/Stop';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // For back button

import { Editor } from '@tinymce/tinymce-react';

import {
  createLiveNews,
  addLiveUpdate,
  endLiveNews,
  getLiveNewsById,
  uploadMediaFiles,
  getRunningLiveNews,
} from "../../../services/adminService";

import {
  getCategories,
  getSubcategories,
  getCountries,
  getStatesByCountry,
  getCitiesByState,
} from "../../../services/apiService";
// const BASE_URL = import.meta.env.VITE_FRONTEND_BASE_URL;
const BASE_URL = process.env.REACT_APP_FRONTEND_BASE_URL;

const LiveNewsManager = () => {
  // State for the 'Create Live News' form
  const [formData, setFormData] = useState({
    title_en: "",
    title_hi: "",
    summary_en: "",
    summary_hi: "",
    content_en: "",
    content_hi: "",
    category: "",
    subCategory: "",
    country: "",
    state: "",
    city: "",
  });

  const [coverFile, setCoverFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // States for managing all running live news and the currently selected one
  const [runningLiveNewsList, setRunningLiveNewsList] = useState([]); // List of all active live news
  const [selectedLiveNews, setSelectedLiveNews] = useState(null); // The specific live news item currently being managed
  const [viewMode, setViewMode] = useState("list"); // "list", "create", "manage"

  // States for adding updates to a live news
  const [text_en, setTextEn] = useState("");
  const [text_hi, setTextHi] = useState("");
  const [files, setFiles] = useState([]); // Files for live updates

  // UI related states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Language field visibility
  const [showEnglishFields, setShowEnglishFields] = useState(true);
  const [showHindiFields, setShowHindiFields] = useState(true);

  // Refs for TinyMCE editors
  const editorRefEn = useRef(null);
  const editorRefHi = useRef(null);

  // TinyMCE Editor Configuration (copied from CreateNews - no change needed here)
  const editorConfig = {
    height: 500,
    menubar: true,
    branding: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount', 'emoticons',
      'template', 'codesample', 'hr', 'pagebreak', 'nonbreaking',
      'toc', 'imagetools', 'textpattern', 'noneditable', 'quickbars',
      'accordion', 'autosave', 'save', 'directionality', 'visualchars'
    ],
    toolbar1: 'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify',
    toolbar2: 'outdent indent | numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen preview save print',
    toolbar3: 'insertfile image media template link anchor codesample | customupload | ltr rtl | showcomments addcomment',

    content_style: `
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      font-size: 14px;
      line-height: 1.6;
      margin: 1rem;
    }
    img { max-width: 100%; height: auto; }
    .mce-content-body {
      padding: 20px;
      max-width: none;
    }
    .left { text-align: left; }
    .center { text-align: center; }
    .right { text-align: right; }
    .full { text-align: justify; }
  `,

    paste_data_images: true,
    automatic_uploads: true,
    file_picker_types: 'image media',

    file_picker_callback: function (callback, value, meta) {
      console.log('File picker triggered for:', meta.filetype);

      const input = document.createElement('input');
      input.setAttribute('type', 'file');

      if (meta.filetype === 'image') {
        input.setAttribute('accept', 'image/*');
      } else if (meta.filetype === 'media') {
        input.setAttribute('accept', 'video/*,audio/*');
      } else {
        input.setAttribute('accept', 'image/*,video/*,audio/*');
      }

      input.onchange = function () {
        const file = this.files[0];

        if (!file) {
          console.log('No file selected');
          return;
        }

        console.log('File selected:', file.name, file.type, file.size);

        const editor = window.tinymce.activeEditor;
        editor.setProgressState(true);

        const formData = new FormData();
        formData.append('mediaFiles', file);

        uploadMediaFiles(formData) // Using the imported uploadMediaFiles
          .then(response => {
            console.log('Upload response:', response);
            editor.setProgressState(false);

            if (response && response.locations && response.locations.length > 0) {
              const uploadedUrl = response.locations[0];
              console.log('File uploaded successfully:', uploadedUrl);
              callback(uploadedUrl, {
                title: file.name,
                alt: file.name
              });

              editor.notificationManager.open({
                text: `File "${file.name}" uploaded successfully!`,
                type: 'success',
                timeout: 3000
              });
            } else {
              throw new Error('No URL returned from upload service');
            }
          })
          .catch(error => {
            console.error('Upload failed:', error);
            editor.setProgressState(false);

            editor.notificationManager.open({
              text: `Upload failed: ${error.message || 'Unknown error'}`,
              type: 'error',
              timeout: 5000
            });

            alert(`File upload failed: ${error.message || 'Please try again'}`);
          });
      };

      input.click();
    },

    images_upload_handler: function (blobInfo, success, failure, progress) {
      console.log('Image upload handler triggered');
      console.log('Blob info:', blobInfo.filename(), blobInfo.blob().type, blobInfo.blob().size);

      const blob = blobInfo.blob();

      const file = new File([blob], blobInfo.filename() || `image-${Date.now()}.png`, {
        type: blob.type || 'image/png'
      });

      console.log('Created file object:', file);

      const formData = new FormData();
      formData.append('mediaFiles', file);

      const editor = window.tinymce.activeEditor;
      editor.setProgressState(true);

      uploadMediaFiles(formData) // Using the imported uploadMediaFiles
        .then(response => {
          console.log('Upload response:', response);
          editor.setProgressState(false);

          if (response && response.locations && response.locations.length > 0) {
            const uploadedUrl = response.locations[0];
            console.log('Image uploaded successfully:', uploadedUrl);

            success(uploadedUrl);

            editor.notificationManager.open({
              text: `Image uploaded successfully!`,
              type: 'success',
              timeout: 3000
            });
          } else {
            throw new Error('No URL returned from upload service');
          }
        })
        .catch(error => {
          console.error('Image upload failed:', error);
          editor.setProgressState(false);

          failure(`Upload failed: ${error.message || 'Unknown error'}`);

          editor.notificationManager.open({
            text: `Upload failed: ${error.message || 'Unknown error'}`,
            type: 'error',
            timeout: 5000
          });
        });
    },

    setup: function (editor) {
      console.log('TinyMCE editor setup starting');

      editor.on('init', function () {
        console.log('TinyMCE editor initialized');
        editor.getContainer().style.transition = "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out";
      });

      // Add custom upload button to toolbar
      editor.ui.registry.addButton('customupload', {
        text: 'Upload Media',
        icon: 'upload',
        tooltip: 'Upload images or videos',
        onAction: function () {
          console.log('Custom upload button clicked');

          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*,video/*');
          input.setAttribute('multiple', 'true');

          input.onchange = function () {
            const files = Array.from(this.files);
            console.log('Multiple files selected:', files.length);

            if (files.length === 0) return;

            editor.setProgressState(true);

            const formData = new FormData();
            files.forEach(file => {
              formData.append('mediaFiles', file);
              console.log('Adding file to FormData:', file.name);
            });

            uploadMediaFiles(formData) // Using the imported uploadMediaFiles
              .then(response => {
                console.log('Batch upload response:', response);
                editor.setProgressState(false);

                if (response && response.locations && response.locations.length > 0) {
                  response.locations.forEach((url, index) => {
                    const file = files[index];
                    if (file && file.type.startsWith('image/')) {
                      editor.insertContent(`<img src="${url}" alt="${file.name}" style="max-width: 100%; height: auto;" />`);
                    } else if (file && file.type.startsWith('video/')) {
                      editor.insertContent(`<video controls style="max-width: 100%; height: auto;">
                      <source src="${url}" type="${file.type}">
                      Your browser does not support the video tag.
                    </video>`);
                    }
                  });

                  editor.notificationManager.open({
                    text: `Successfully uploaded ${response.locations.length} file(s)!`,
                    type: 'success',
                    timeout: 3000
                  });
                } else {
                  throw new Error('No URLs returned from upload service');
                }
              })
              .catch(error => {
                console.error('Batch upload failed:', error);
                editor.setProgressState(false);

                editor.notificationManager.open({
                  text: `Upload failed: ${error.message || 'Unknown error'}`,
                  type: 'error',
                  timeout: 5000
                });
              });
          };

          input.click();
        }
      });

      editor.on('drop', function (e) {
        console.log('File(s) dropped on editor');
      });

      editor.on('paste', function (e) {
        console.log('Content pasted into editor');
      });
    },

    contextmenu: 'link image imagetools table',
    quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
    quickbars_insert_toolbar: 'quickimage quicktable customupload',
    powerpaste_word_import: 'clean',
    powerpaste_html_import: 'clean',

    image_advtab: true,
    image_uploadtab: true,
    image_caption: true,
    image_title: true,

    template_cdate_format: '[Date Created (CDATE): %m/%d/%Y : %H:%M:%S]',
    template_mdate_format: '[Date Modified (MDATE): %m/%d/%Y : %H:%M:%S]',

    formats: {
      alignleft: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'left' },
      aligncenter: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'center' },
      alignright: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'right' },
      alignjustify: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'full' },
      bold: { inline: 'strong' },
      italic: { inline: 'em' },
      underline: { inline: 'u' },
      strikethrough: { inline: 'del' },
      customformat: { inline: 'span', styles: { color: '#00ff00', fontSize: '20px' }, attributes: { title: 'My custom format' }, classes: 'example1' }
    }
  };


  // ====================== FETCH INITIAL DATA (Categories, Countries) ======================
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [catRes, countryRes] = await Promise.all([
          getCategories(),
          getCountries(),
        ]);

        const catData = catRes.data || catRes.data?.data || catRes || [];
        const countryData = countryRes.data || countryRes.data?.data || countryRes || [];

        setCategories(catData);
        setCountries(countryData);
      } catch (err) {
        console.error("Error loading categories or countries:", err);
        setError("Failed to load initial data (categories/countries).");
      }
    };
    fetchInitialData();
  }, []);

  // ====================== FETCH ALL RUNNING LIVE NEWS (List View) ======================
  const fetchRunningLiveNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRunningLiveNews();
      setRunningLiveNewsList(res.data?.data || res.data || []); // Ensure it's an array and handle nested data
    } catch (err) {
      console.error("Error fetching running live news list:", err);
      setError(err.response?.data?.message || "Failed to load running live news list.");
      setRunningLiveNewsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effect to fetch running live news on mount and periodically refresh
  useEffect(() => {
    if (viewMode === "list") { // Only refresh the list when in list view
      fetchRunningLiveNews();
      const interval = setInterval(fetchRunningLiveNews, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [viewMode, fetchRunningLiveNews]);


  // ====================== HANDLE CASCADING DROPDOWNS ======================
  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setFormData((prev) => ({ ...prev, category: categoryId, subCategory: "" }));
    setFormErrors((prev) => ({ ...prev, category: "" }));

    if (categoryId) {
      try {
        const res = await getSubcategories(categoryId);
        const data = res.data?.data || res.data || [];
        setSubCategories(data);
      } catch (err) {
        console.error("Error loading subcategories:", err);
        setError("Failed to load subcategories.");
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  };

  const handleCountryChange = async (e) => {
    const countryId = e.target.value;
    setFormData((prev) => ({ ...prev, country: countryId, state: "", city: "" }));
    setFormErrors((prev) => ({ ...prev, country: "" }));

    if (countryId) {
      try {
        const res = await getStatesByCountry(countryId);
        const data = res.data || res.data?.data || res || [];
        setStates(data);
        setCities([]);
      } catch (err) {
        console.error("Error loading states:", err);
        setError("Failed to load states.");
        setStates([]);
        setCities([]);
      }
    } else {
      setStates([]);
      setCities([]);
    }
  };

  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setFormData((prev) => ({ ...prev, state: stateId, city: "" }));
    setFormErrors((prev) => ({ ...prev, state: "" }));

    if (stateId) {
      try {
        const res = await getCitiesByState(stateId);
        const data = res.data || res.data?.data || res || [];
        setCities(data);
      } catch (err) {
        console.error("Error loading cities:", err);
        setError("Failed to load cities.");
        setCities([]);
      }
    } else {
      setCities([]);
    }
  };

  // Generic handleChange for form fields
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  // Handler for TinyMCE editor content change
  const handleEditorChange = (content, name) => {
    setFormData(prev => ({ ...prev, [name]: content }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };


  // ====================== CLIENT-SIDE FORM VALIDATION ======================
  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!showEnglishFields && !showHindiFields) {
      errors.languageSelection = 'Please select at least one language (English or Hindi) to start live news.';
      isValid = false;
    }

    if (showEnglishFields) {
      if (!formData.title_en.trim()) { errors.title_en = 'Title (English) is required.'; isValid = false; }
      if (!formData.summary_en.trim()) { errors.summary_en = 'Summary (English) is required.'; isValid = false; }
      else if (formData.summary_en.trim().length > 500) { errors.summary_en = 'Summary (English) cannot exceed 500 characters.'; isValid = false; }
      if (!formData.content_en.trim()) { errors.content_en = 'Content (English) is required.'; isValid = false; }
      else if (formData.content_en.trim().length > 1000000) { errors.content_en = 'Content (English) cannot exceed 1,000,000 characters.'; isValid = false; }
    }

    if (showHindiFields) {
      if (!formData.title_hi.trim()) { errors.title_hi = 'शीर्षक (हिंदी) आवश्यक है।'; isValid = false; }
      if (!formData.summary_hi.trim()) { errors.summary_hi = 'सारांश (हिंदी) आवश्यक है।'; isValid = false; }
      else if (formData.summary_hi.trim().length > 500) { errors.summary_hi = 'सारांश (हिंदी) 500 अक्षरों से अधिक नहीं हो सकता।'; isValid = false; }
      if (!formData.content_hi.trim()) { errors.content_hi = 'सामग्री (हिंदी) आवश्यक है।'; isValid = false; }
      else if (formData.content_hi.trim().length > 1000000) { errors.content_hi = 'सामग्री (हिंदी) 1,000,000 अक्षरों से अधिक नहीं हो सकती।'; isValid = false; }
    }

    const hasValidEnglish = showEnglishFields && formData.title_en.trim() && formData.summary_en.trim() && formData.content_en.trim();
    const hasValidHindi = showHindiFields && formData.title_hi.trim() && formData.summary_hi.trim() && formData.content_hi.trim();

    if (!hasValidEnglish && !hasValidHindi && (showEnglishFields || showHindiFields)) {
      errors.noValidContent = 'At least one of English or Hindi live news must have a title, summary and content.';
      isValid = false;
    }

    if (!formData.category) { errors.category = 'Category is required.'; isValid = false; }
    if (!formData.country) { errors.country = 'Country is required.'; isValid = false; }

    setFormErrors(errors);
    return isValid;
  };


  // ====================== CREATE LIVE NEWS ======================
  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setFormErrors({});

    if (!validateForm()) {
      setLoading(false);
      setError("Please correct the errors in the form.");
      return;
    }

    try {
      const data = new FormData();

      if (showEnglishFields) {
        data.append('title_en', formData.title_en);
        data.append('summary_en', formData.summary_en);
        data.append('content_en', formData.content_en);
      } else {
        data.append('title_en', '');
        data.append('summary_en', '');
        data.append('content_en', '');
      }

      if (showHindiFields) {
        data.append('title_hi', formData.title_hi);
        data.append('summary_hi', formData.summary_hi);
        data.append('content_hi', formData.content_hi);
      } else {
        data.append('title_hi', '');
        data.append('summary_hi', '');
        data.append('content_hi', '');
      }

      data.append('category', formData.category);
      if (formData.subCategory) data.append('subCategory', formData.subCategory);
      data.append('country', formData.country);
      if (formData.state) data.append('state', formData.state);
      if (formData.city) data.append('city', formData.city);

      if (coverFile) data.append("coverImage", coverFile);

      const res = await createLiveNews(data);
      setSelectedLiveNews(res.data?.data || res.data || res); // Set the newly created live news as selected
      setSuccessMessage("✅ Live News Started Successfully!");
      setError(null);

      // Reset form fields
      setFormData({
        title_en: "", title_hi: "", summary_en: "", summary_hi: "",
        content_en: "", content_hi: "", category: "", subCategory: "",
        country: "", state: "", city: "",
      });
      setCoverFile(null);
      setSubCategories([]);
      setStates([]);
      setCities([]);
      if (editorRefEn.current) editorRefEn.current.setContent('');
      if (editorRefHi.current) editorRefHi.current.setContent('');

      fetchRunningLiveNews(); // Refresh the list of running live news
      setViewMode("manage"); // Switch to manage the newly created live news

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to create live news");
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  // ====================== ADD LIVE UPDATE ======================
  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (!selectedLiveNews?._id) {
        setError("No live news selected to add update.");
        return;
    }

    // Basic validation for updates
    if (!text_en.trim() && !text_hi.trim() && files.length === 0) {
      setError("Please add some text or media for the update.");
      return;
    }

    const data = new FormData();
    data.append("text_en", text_en);
    data.append("text_hi", text_hi);
    files.forEach((file) => data.append("media", file));

    try {
      setLoading(true);
      const res = await addLiveUpdate(selectedLiveNews._id, data);
      setSelectedLiveNews(res.data?.data || res.data || res); // Update the currently managed live news
      setTextEn("");
      setTextHi("");
      setFiles([]); // Clear selected files
      setSuccessMessage("Live update added successfully");
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to add update");
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  // ====================== FETCH SELECTED LIVE NEWS FOR MANAGEMENT (Auto-refresh) ======================
  const fetchSelectedLive = useCallback(async () => {
    if (!selectedLiveNews?._id || selectedLiveNews.status === "ended") return;
    try {
      const res = await getLiveNewsById(selectedLiveNews._id);
      setSelectedLiveNews(res.data?.data || res.data || res); // Update selected live news with latest data
    } catch (err) {
      console.error(err);
      // If it ends or is not found while managing, go back to list view
      if (err.response?.status === 404 || err.response?.data?.message === "This live session has ended") {
        setSelectedLiveNews(null);
        setViewMode("list");
        fetchRunningLiveNews(); // Refresh the main list
        setError("The live session you were managing has ended or is no longer available.");
      }
    }
  }, [selectedLiveNews?._id, selectedLiveNews?.status, fetchRunningLiveNews]);

  // Effect to auto-refresh the currently managed live news details
  useEffect(() => {
    if (viewMode === "manage" && selectedLiveNews?._id && selectedLiveNews.status !== "ended") {
      const interval = setInterval(fetchSelectedLive, 10000); // Fetch every 10 seconds
      return () => clearInterval(interval);
    }
  }, [viewMode, selectedLiveNews?._id, selectedLiveNews?.status, fetchSelectedLive]);


  // ====================== END LIVE NEWS ======================
  const handleEndLive = async (liveNewsIdToEnd) => {
    const idToProcess = liveNewsIdToEnd || selectedLiveNews?._id;
    if (!idToProcess) {
        setError("No live news selected to end!");
        return;
    }
    if (!window.confirm("Are you sure you want to end this live news? It will be converted into a regular news article.")) {
      return;
    }

    try {
      setLoading(true);
      await endLiveNews(idToProcess);
      setSuccessMessage("🛑 Live ended successfully and converted to news!");
      setError(null);

      // If the one being managed was ended, clear selection and go to list
      if (selectedLiveNews?._id === idToProcess) {
        setSelectedLiveNews(null);
        setViewMode("list");
      }
      fetchRunningLiveNews(); // Refresh the list of running live news

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to end live news");
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  // ====================== HANDLE MANAGING AN EXISTING LIVE NEWS ======================
  const handleManageLiveNews = async (newsItem) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      // Fetch the full details of the live news, as the list item might be a summary
      const res = await getLiveNewsById(newsItem._id);
      setSelectedLiveNews(res.data?.data || res.data || res);
      setViewMode("manage");
    } catch (err) {
      console.error("Error fetching live news for management:", err);
      setError(err.response?.data?.message || "Failed to load live news for management. It might have ended.");
      setSelectedLiveNews(null);
      setViewMode("list"); // Fallback to list view if fetch fails
    } finally {
      setLoading(false);
    }
  };

  // ====================== UI RENDERING LOGIC ======================
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" fontWeight={600} gutterBottom align="center" sx={{ mb: 4 }}>
        📰 Live News Control Panel
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {/* Main List View */}
      {viewMode === "list" && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={500}>
              Currently Running Live News ({runningLiveNewsList.length})
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                setViewMode("create");
                // Reset form data when switching to create
                setFormData({
                    title_en: "", title_hi: "", summary_en: "", summary_hi: "",
                    content_en: "", content_hi: "", category: "", subCategory: "",
                    country: "", state: "", city: "",
                });
                setCoverFile(null);
                setFormErrors({});
                if (editorRefEn.current) editorRefEn.current.setContent('');
                if (editorRefHi.current) editorRefHi.current.setContent('');
              }}
            >
              Start New Live News
            </Button>
          </Stack>

          {loading && !runningLiveNewsList.length && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
          {!loading && runningLiveNewsList.length === 0 ? (
            <Alert severity="info">No live news currently running. Click "Start New Live News" to begin.</Alert>
          ) : (
            // Replaced Grid/Card with Table
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="running live news table">
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Started At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runningLiveNewsList.map((newsItem) => (
                    <TableRow
                      key={newsItem._id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {newsItem.coverImage?.url ? (
                          <img
                            src={newsItem.coverImage.url}
                            alt={newsItem.title_en || "Live News Cover"}
                            style={{ width: '80px', height: 'auto', borderRadius: '4px' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">No Image</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {newsItem.title_en || newsItem.title_hi || "Untitled Live News"}
                        </Typography>
                        {newsItem.title_en && newsItem.title_hi && (
                          <Typography variant="caption" color="text.secondary">
                            {newsItem.title_hi}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {newsItem.category?.name_en || newsItem.category?.name_hi || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {new Date(newsItem.startedAt).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleManageLiveNews(newsItem)}
                            disabled={loading}
                          >
                            {/* Manage */}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<StopIcon />}
                            onClick={() => handleEndLive(newsItem._id)}
                            disabled={loading}
                          >
                            End Live
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Create or Manage View */}
      {(viewMode === "create" || viewMode === "manage") && (
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setViewMode("list");
              setSelectedLiveNews(null); // Clear selected news when going back to list
              setError(null); // Clear errors when navigating away
              setSuccessMessage(null); // Clear success when navigating away
            }}
            sx={{ mb: 3 }}
          >
            Back to All Live News
          </Button>

          {viewMode === "create" ? (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={500} gutterBottom>
                Start New Live News
              </Typography>
              <Box component="form" onSubmit={handleCreate} sx={{ mt: 3 }}>
                {/* Language Selection Checkboxes */}
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Select Content Languages:
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showEnglishFields}
                          onChange={(e) => setShowEnglishFields(e.target.checked)}
                          name="showEnglishFields"
                          color="primary"
                        />
                      }
                      label="English Fields"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showHindiFields}
                          onChange={(e) => setShowHindiFields(e.target.checked)}
                          name="showHindiFields"
                          color="primary"
                        />
                      }
                      label="Hindi Fields"
                    />
                  </Stack>
                  {formErrors.languageSelection && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {formErrors.languageSelection}
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ my: 3 }} />


                {/* English Fields */}
                {showEnglishFields && (
                  <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                      English Content
                    </Typography>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Title (English)"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      error={!!formErrors.title_en}
                      helperText={formErrors.title_en}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Summary (English)"
                      name="summary_en"
                      value={formData.summary_en}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      inputProps={{ maxLength: 500 }}
                      error={!!formErrors.summary_en}
                      helperText={formErrors.summary_en}
                    />
                    <Box sx={{ mt: 3, mb: 3 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                        Content (English) * (MS Word-like Editor)
                      </Typography>
                      <Box sx={{ border: formErrors.content_en ? '1px solid #f44336' : '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Editor
                          apiKey="tdqbnxhth99nqmhdy4dmearws4a1teixkl1220nwcnxwhro9"
                          onInit={(evt, editor) => editorRefEn.current = editor}
                          init={editorConfig}
                          value={formData.content_en}
                          onEditorChange={(content) => handleEditorChange(content, 'content_en')}
                        />
                      </Box>
                      {formErrors.content_en && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                          {formErrors.content_en}
                        </Typography>
                      )}
                     
                    </Box>
                  </Box>
                )}

                {/* Hindi Fields */}
                {showHindiFields && (
                  <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                      हिंदी सामग्री
                    </Typography>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="शीर्षक (हिंदी)"
                      name="title_hi"
                      value={formData.title_hi}
                      onChange={handleChange}
                      error={!!formErrors.title_hi}
                      helperText={formErrors.title_hi}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      label="सारांश (हिंदी)"
                      name="summary_hi"
                      value={formData.summary_hi}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      inputProps={{ maxLength: 500 }}
                      error={!!formErrors.summary_hi}
                      helperText={formErrors.summary_hi}
                    />
                    <Box sx={{ mt: 3, mb: 3 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                        सामग्री (हिंदी) * (MS Word जैसा एडिटर)
                      </Typography>
                      <Box sx={{ border: formErrors.content_hi ? '1px solid #f44336' : '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Editor
                          apiKey="tdqbnxhth99nqmhdy4dmearws4a1teixkl1220nwcnxwhro9"
                          onInit={(evt, editor) => editorRefHi.current = editor}
                          init={editorConfig}
                          value={formData.content_hi}
                          onEditorChange={(content) => handleEditorChange(content, 'content_hi')}
                        />
                      </Box>
                      {formErrors.content_hi && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                          {formErrors.content_hi}
                        </Typography>
                      )}
                    
                    </Box>
                  </Box>
                )}

                {formErrors.noValidContent && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formErrors.noValidContent}
                  </Alert>
                )}

                {/* ========== CATEGORY & SUBCATEGORY ========== */}
                <TextField
                  fullWidth
                  select
                  margin="normal"
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleCategoryChange}
                  error={!!formErrors.category}
                  helperText={formErrors.category}
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name || cat.name_en || cat.name_hi}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  select
                  margin="normal"
                  label="SubCategory"
                  name="subCategory"
                  value={formData.subCategory}
                  onChange={handleChange}
                  disabled={!formData.category || subCategories.length === 0}
                >
                  <MenuItem value="">None</MenuItem>
                  {subCategories.map((sub) => (
                    <MenuItem key={sub._id} value={sub._id}>
                      {sub.name}
                    </MenuItem>
                  ))}
                </TextField>

                {/* ========== LOCATION FIELDS ========== */}
                <TextField
                  fullWidth
                  select
                  margin="normal"
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleCountryChange}
                  error={!!formErrors.country}
                  helperText={formErrors.country}
                >
                  <MenuItem value="">Select Country</MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  select
                  margin="normal"
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleStateChange}
                  disabled={!formData.country || states.length === 0}
                >
                  <MenuItem value="">None</MenuItem>
                  {states.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  select
                  margin="normal"
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.state || cities.length === 0}
                >
                  <MenuItem value="">None</MenuItem>
                  {cities.map((ct) => (
                    <MenuItem key={ct._id} value={ct._id}>
                      {ct.name}
                    </MenuItem>
                  ))}
                </TextField>

                {/* ✅ Cover Image Upload */}
                <Typography variant="body2" mt={2}>
                  Upload Cover Image (optional)
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverFile(e.target.files[0])}
                  style={{ marginTop: "8px" }}
                />
                 {coverFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Selected: {coverFile.name}
                  </Typography>
                )}


                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Start Live News"}
                </Button>
              </Box>
            </Paper>
          ) : (
            selectedLiveNews && ( // Only render if in manage mode and selectedLiveNews exists
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={500} gutterBottom>
                  Managing Live News
                  <span style={{ color: "red", marginLeft: '8px', animation: "blink 1s infinite" }}>● LIVE</span>
                </Typography>
                <Box sx={{ p: 2, mt: 3, border: "1px solid #ccc", borderRadius: 2 }}>
                  <Typography variant="h5">
                    {selectedLiveNews.title_en || selectedLiveNews.title_hi || "Untitled Live News"}
                  </Typography>

   
{selectedLiveNews.coverImage?.url && (
  <Box 
    sx={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      p: 2
    }}
  >
    <CardMedia
      component="img"
      image={selectedLiveNews.coverImage.url}
      alt={selectedLiveNews.title_en || "Live News Cover"}
      sx={{
        borderRadius: 2,
        maxWidth: '40%',
        maxHeight: 400,
        objectFit: 'contain',   // <-- Image full show
        margin: '0 auto',       // <-- center
      }}
    />
  </Box>
)}

                  {/*
                    The original Typography component would render HTML as plain text.
                    To correctly render HTML from content_hi or content_en,
                    we use dangerouslySetInnerHTML within a Box component.
                  */}
                  {(selectedLiveNews.content_hi || selectedLiveNews.content_en) && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: '1px solid #eee',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        maxHeight: '400px', // Optional: Set max height
                        overflowY: 'auto',   // Optional: Add scroll if content exceeds max height
                        '& img': { maxWidth: '100%', height: 'auto' }, // Ensure images are responsive
                        '& video': { maxWidth: '100%', height: 'auto' }, // Ensure videos are responsive
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedLiveNews.content_hi || selectedLiveNews.content_en
                      }}
                    />
                  )}

                  <Typography variant="body2" mt={1} sx={{ color: "blue" }}>
                    Share Link:{" "}
                    <a
                      // href={`https://news.aasmo.in/live/${selectedLiveNews.slug_en}`}
                       href={`${BASE_URL}/live/${selectedLiveNews.slug_en}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {/* https://news.aasmo.in/live/{selectedLiveNews.slug_en} */}
                      {BASE_URL}/live/{selectedLiveNews.slug_en}
                    </a>
                  </Typography>

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleEndLive(selectedLiveNews._id)}
                    sx={{ mt: 2 }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "End Live News"}
                  </Button>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* ========== ADD LIVE UPDATE ========== */}
                {selectedLiveNews.status !== "ended" && (
                  <Box component="form" onSubmit={handleAddUpdate}>
                    <Typography variant="h6">➕ Add Live Update</Typography>
                    <TextField
                      fullWidth
                      multiline
                      label="Text (English)"
                      value={text_en}
                      onChange={(e) => setTextEn(e.target.value)}
                      margin="normal"
                      rows={2}
                    />
                    <TextField
                      fullWidth
                      multiline
                      label="Text (Hindi)"
                      value={text_hi}
                      onChange={(e) => setTextHi(e.target.value)}
                      margin="normal"
                      rows={2}
                    />
                    <Typography variant="body2" mt={2} mb={1}>
                      Upload Media for Update (optional, max 10 files)
                    </Typography>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={(e) => setFiles(Array.from(e.target.files))}
                      style={{ marginTop: "10px", marginBottom: "10px" }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, mb: 2 }}>
                      {files.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => setFiles(prev => prev.filter((_, i) => i !== index))}
                          sx={{ maxWidth: '150px', mb: 1 }} // Added margin-bottom for wrap
                        />
                      ))}
                    </Stack>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ mt: 2 }}
                      disabled={loading || (!text_en.trim() && !text_hi.trim() && files.length === 0)}
                    >
                      {loading ? <CircularProgress size={24} /> : "Add Update"}
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" gutterBottom>
                  📢 Live Updates
                </Typography>
                {selectedLiveNews.updates?.length === 0 ? (
                  <Typography color="text.secondary">No updates yet...</Typography>
                ) : (
                  // Sort updates to show newest first for management view
                  [...selectedLiveNews.updates].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((update, i) => (
                    <Card key={update._id || i} sx={{ mb: 2, borderLeft: '4px solid #1976d2' }}>
                      <CardContent>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {update.text_hi || update.text_en}
                        </Typography>
                       {update.media?.map((m, idx) =>
  m.type === "image" ? (
    <Box
      key={idx}
      sx={{
        width: "30%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mt: 1,
      }}
    >
      <CardMedia
        component="img"
        image={m.url}
        alt={m.caption_en || m.caption || "Update Image"}
        sx={{
          maxWidth: "100%",
          maxHeight: 400,          // bigger image view
          objectFit: "contain",    // FULL image, NO CROP
          borderRadius: 2,
        }}
      />
    </Box>
  ) : (
    <video
      key={idx}
      src={m.url}
      controls
      style={{
        width: "100%",
        marginTop: "8px",
        borderRadius: "8px",
      }}
    />
  )
)}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {new Date(update.createdAt).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Paper>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default LiveNewsManager;